/* eslint-disable react/no-unescaped-entities */
import { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { toast } from 'sonner';
import { Save, Building, Clock, UserPlus, AlertCircle, Globe, Mail, Eye, EyeOff, BellRing, MessageCircle, CreditCard, Settings } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { PageTitle } from '@/components/backoffice/PageTitle';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DAYS = ['Lunedì', 'Martedì', 'Mercoledì', 'Giovedì', 'Venerdì', 'Sabato', 'Domenica'];

export default function Impostazioni() {
  const { user } = useAuth();

  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  // Create operator
  const [operatorDialogOpen, setOperatorDialogOpen] = useState(false);
  const [operatorForm, setOperatorForm] = useState({ email: '', password: '', nome: '', cognome: '' });
  const [operatorError, setOperatorError] = useState('');
  const [operatorSaving, setOperatorSaving] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [testingSMTP, setTestingSMTP] = useState(false);
  const [showSmtpPassword, setShowSmtpPassword] = useState(false);
  const [showStripeKey, setShowStripeKey] = useState(false);
  const [smtpTestLog, setSmtpTestLog] = useState(null);
  const [sendingReminders, setSendingReminders] = useState(false);
  useEffect(() => {
    axios.get(`${API}/settings`, {})
      .then(r => setSettings(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleChange = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  const toggleDay = (dayIndex) => {
    setSettings(prev => {
      const orari = [...(prev.orari || [])];
      const idx = orari.findIndex(o => o.day_of_week === dayIndex);
      if (idx >= 0) {
        orari[idx] = { ...orari[idx], is_open: !orari[idx].is_open };
      } else {
        orari.push({ day_of_week: dayIndex, start_time: '09:00', end_time: '19:00', is_open: true });
      }
      return { ...prev, orari };
    });
  };

  const updateDayTime = (dayIndex, field, value) => {
    setSettings(prev => ({
      ...prev,
      orari: (prev.orari || []).map(o => o.day_of_week === dayIndex ? { ...o, [field]: value } : o)
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/settings`, settings, {});
      toast.success('Impostazioni salvate');
    } catch { toast.error('Errore nel salvataggio'); }
    finally { setSaving(false); }
  };

  const handleCreateOperator = async (e) => {
    e.preventDefault();
    setOperatorError('');
    setOperatorSaving(true);
    try {
      await axios.post(`${API}/users/create-operator`, operatorForm, {});
      toast.success(`Operatore ${operatorForm.nome} ${operatorForm.cognome} creato con successo`);
      setOperatorDialogOpen(false);
      setOperatorForm({ email: '', password: '', nome: '', cognome: '' });
    } catch (err) {
      setOperatorError(err.response?.data?.detail || 'Errore nella creazione');
    } finally { setOperatorSaving(false); }
  };

  if (loading) return <div className="space-y-4">{[1,2,3].map(i => <Skeleton key={i} className="h-32 rounded-xl" />)}</div>;

  return (
    <div>
      <div className="mb-6">
        <PageTitle title="Impostazioni Studio" info="Informazioni dello studio, orari, canali di notifica (email/WhatsApp), pagamenti e integrazioni." />
      </div>

      <div className="space-y-6">
        {/* Studio Info */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2" style={{fontFamily:'Space Grotesk'}}><Building size={20} /> Informazioni Studio</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2"><Label>Nome Studio</Label><Input value={settings?.nome_studio || ''} onChange={e => handleChange('nome_studio', e.target.value)} /></div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Indirizzo</Label><Input value={settings?.indirizzo || ''} onChange={e => handleChange('indirizzo', e.target.value)} /></div>
              <div className="space-y-2"><Label>Città</Label><Input value={settings?.citta || ''} onChange={e => handleChange('citta', e.target.value)} /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Telefono 1</Label><Input value={settings?.telefono1 || ''} onChange={e => handleChange('telefono1', e.target.value)} /></div>
              <div className="space-y-2"><Label>Telefono 2</Label><Input value={settings?.telefono2 || ''} onChange={e => handleChange('telefono2', e.target.value)} /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Email</Label><Input value={settings?.email || ''} onChange={e => handleChange('email', e.target.value)} /></div>
              <div className="space-y-2"><Label>Partita IVA</Label><Input value={settings?.partita_iva || ''} onChange={e => handleChange('partita_iva', e.target.value)} /></div>
            </div>
            <div className="space-y-2"><Label>Descrizione</Label><Textarea value={settings?.descrizione || ''} onChange={e => handleChange('descrizione', e.target.value)} rows={3} /></div>
          </CardContent>
        </Card>

        {/* Working Hours */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2" style={{fontFamily:'Space Grotesk'}}><Clock size={20} /> Orari di Apertura</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {DAYS.map((day, i) => {
                const dayData = settings?.orari?.find(o => o.day_of_week === i);
                return (
                  <div key={`day-${i}`} className="flex items-center gap-3">
                    <label className="flex items-center gap-2 w-28 text-sm cursor-pointer">
                      <Checkbox checked={dayData?.is_open || false} onCheckedChange={() => toggleDay(i)} />
                      {day}
                    </label>
                    {dayData?.is_open ? (
                      <>
                        <Input type="time" value={dayData.start_time || '09:00'} onChange={e => updateDayTime(i, 'start_time', e.target.value)} className="w-28 h-8" />
                        <span className="text-sm">-</span>
                        <Input type="time" value={dayData.end_time || '19:00'} onChange={e => updateDayTime(i, 'end_time', e.target.value)} className="w-28 h-8" />
                      </>
                    ) : (
                      <span className="text-sm text-muted-foreground">Chiuso</span>
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Global Site Actions */}
        {user?.role === 'admin' && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2" style={{fontFamily:'Space Grotesk'}}><Globe size={20} /> Azioni Generali del Sito</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between py-3 border-b">
                <div>
                  <p className="text-sm font-medium">Prenotazioni attive</p>
                  <p className="text-xs text-muted-foreground">Se disattivato, la pagina di prenotazione mostrerà un messaggio con rimando alla pagina Contatti</p>
                </div>
                <Checkbox checked={settings?.prenotazioni_attive !== false} onCheckedChange={v => handleChange('prenotazioni_attive', v)} />
              </div>
              <div className="flex items-center justify-between py-3 border-b">
                <div>
                  <p className="text-sm font-medium">Mostra prezzi sul sito</p>
                  <p className="text-xs text-muted-foreground">Se disattivato, i prezzi non saranno visibili a nessun utente sul sito pubblico</p>
                </div>
                <Checkbox checked={settings?.mostra_prezzi_globale === true} onCheckedChange={v => handleChange('mostra_prezzi_globale', v)} />
              </div>
              <div className="flex items-center justify-between py-3 border-b">
                <div>
                  <p className="text-sm font-medium">Mostra disponibilità medici</p>
                  <p className="text-xs text-muted-foreground">Se disattivato, gli orari di disponibilità dei medici non saranno visibili nella pagina Equipe</p>
                </div>
                <Checkbox checked={settings?.mostra_disponibilita_globale !== false} onCheckedChange={v => handleChange('mostra_disponibilita_globale', v)} />
              </div>
              <div className="py-3 space-y-2">
                <div>
                  <p className="text-sm font-medium">Regole per la richiesta di appuntamento (chat)</p>
                  <p className="text-xs text-muted-foreground">Istruzioni che l'assistente della chat "Richiesta Appuntamento" del sito seguirà sempre. Una regola per riga, es.: "Chiedi sempre il motivo della visita prima di proporre orari", "Non proporre appuntamenti oltre 30 giorni", "Ricorda di portare la tessera sanitaria".</p>
                </div>
                <Textarea
                  value={settings?.regole_richiesta_appuntamento || ''}
                  onChange={e => handleChange('regole_richiesta_appuntamento', e.target.value)}
                  rows={5}
                  placeholder={"es.\n- Chiedi sempre il motivo della visita\n- Ricorda al paziente di arrivare 10 minuti prima\n- Per le prime visite chiedi se ha documentazione medica da portare"}
                  data-testid="booking-rules-textarea"
                />
              </div>
            </CardContent>
          </Card>
        )}

        {/* Operator Management */}
        {user?.role === 'admin' && (
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2" style={{fontFamily:'Space Grotesk'}}><UserPlus size={20} /> Gestione Operatori</CardTitle>
                <Button onClick={() => setOperatorDialogOpen(true)} className="bg-[#238A92] hover:bg-[#0F5E66] text-white" size="sm">
                  <UserPlus size={14} className="mr-1" /> Nuovo Operatore
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">Crea account operatore per il personale del backoffice. Gli operatori possono gestire appuntamenti e visualizzare i pazienti.</p>
            </CardContent>
          </Card>
        )}

        {/* WhatsApp: la configurazione è stata spostata nella pagina WhatsApp (icona ingranaggio) */}
        {user?.role === 'admin' && (
          <Card data-testid="wa-settings-moved-card">
            <CardContent className="py-4 flex flex-col sm:flex-row sm:items-center gap-3">
              <div className="flex items-center gap-3 flex-1">
                <div className="h-10 w-10 rounded-lg bg-[hsl(var(--secondary))] flex items-center justify-center text-[#0F5E66] flex-shrink-0"><MessageCircle size={20} /></div>
                <div>
                  <p className="text-sm font-semibold" style={{fontFamily:'Space Grotesk'}}>Configurazione WhatsApp</p>
                  <p className="text-xs text-muted-foreground">Motore di invio (Twilio/WAHA), collegamento del server, QR e interruttori generali si gestiscono nella pagina dedicata, raggiungibile anche dall'icona ingranaggio nella pagina WhatsApp.</p>
                </div>
              </div>
              <Link to="/backoffice/whatsapp/configurazione">
                <Button size="sm" variant="outline" data-testid="wa-settings-goto-button"><Settings size={14} className="mr-1" /> Apri configurazione</Button>
              </Link>
            </CardContent>
          </Card>
        )}

        {/* Notifiche e Promemoria */}
        {user?.role === 'admin' && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2" style={{fontFamily:'Space Grotesk'}}><BellRing size={20} /> Notifiche e Promemoria</CardTitle>
            </CardHeader>
            <CardContent className="space-y-1">
              <p className="text-sm text-muted-foreground pb-2">Per ogni notifica scegli il canale di invio: Email (richiede SMTP attivo), WhatsApp (richiede motore WhatsApp configurato) o entrambi.</p>

              <div className="flex items-center justify-between gap-4 py-3 border-t">
                <div>
                  <p className="text-sm font-medium">Conferma prenotazione al paziente</p>
                  <p className="text-xs text-muted-foreground">Inviata quando viene registrata una nuova prenotazione (sito, backoffice o chat WhatsApp)</p>
                </div>
                <Select value={settings?.conferma_prenotazione_canale || 'email'} onValueChange={v => handleChange('conferma_prenotazione_canale', v)}>
                  <SelectTrigger className="w-44 flex-shrink-0" data-testid="notify-conferma-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="off">Disattivato</SelectItem>
                    <SelectItem value="email">Solo Email</SelectItem>
                    <SelectItem value="whatsapp">Solo WhatsApp</SelectItem>
                    <SelectItem value="both">Email + WhatsApp</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center justify-between gap-4 py-3 border-t">
                <div>
                  <p className="text-sm font-medium">Notifiche ai medici</p>
                  <p className="text-xs text-muted-foreground">Il medico riceve un avviso per ogni nuova prenotazione (per WhatsApp serve il telefono nella scheda medico)</p>
                </div>
                <Select value={settings?.notifiche_medici_canale || 'email'} onValueChange={v => handleChange('notifiche_medici_canale', v)}>
                  <SelectTrigger className="w-44 flex-shrink-0" data-testid="notify-medici-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="off">Disattivato</SelectItem>
                    <SelectItem value="email">Solo Email</SelectItem>
                    <SelectItem value="whatsapp">Solo WhatsApp</SelectItem>
                    <SelectItem value="both">Email + WhatsApp</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center justify-between gap-4 py-3 border-t">
                <div>
                  <p className="text-sm font-medium">Promemoria ai pazienti</p>
                  <p className="text-xs text-muted-foreground">Promemoria automatico il giorno prima dell'appuntamento</p>
                </div>
                <Select value={settings?.promemoria_canale || 'email'} onValueChange={v => handleChange('promemoria_canale', v)}>
                  <SelectTrigger className="w-44 flex-shrink-0" data-testid="notify-promemoria-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="off">Disattivato</SelectItem>
                    <SelectItem value="email">Solo Email</SelectItem>
                    <SelectItem value="whatsapp">Solo WhatsApp</SelectItem>
                    <SelectItem value="both">Email + WhatsApp</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {(settings?.promemoria_canale || 'email') !== 'off' && (
                <div className="flex items-center justify-between py-3 border-t gap-4">
                  <div>
                    <p className="text-sm font-medium">Orario di invio promemoria</p>
                    <p className="text-xs text-muted-foreground">I promemoria per gli appuntamenti di domani partono a partire da quest'ora</p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <Input
                      type="number"
                      min={0}
                      max={23}
                      value={settings?.promemoria_ora ?? 9}
                      onChange={e => handleChange('promemoria_ora', Math.max(0, Math.min(23, parseInt(e.target.value, 10) || 0)))}
                      className="w-20"
                      data-testid="reminder-hour-input"
                    />
                    <span className="text-sm text-muted-foreground">:00</span>
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={sendingReminders}
                      onClick={async () => {
                        setSendingReminders(true);
                        try {
                          const res = await axios.post(`${API}/settings/reminders/run-now`);
                          if (res.data.reason === 'smtp_disabled') {
                            toast.error('SMTP disattivato: abilita e configura SMTP per inviare i promemoria via email');
                          } else if (res.data.reason === 'whatsapp_non_configurato') {
                            toast.error('WhatsApp non configurato: configura il motore WhatsApp per inviare i promemoria');
                          } else if (res.data.reason === 'promemoria_disattivati') {
                            toast.info('Promemoria disattivati: scegli un canale di invio e salva');
                          } else if (res.data.candidates === 0) {
                            toast.info('Nessun appuntamento di domani da ricordare');
                          } else {
                            toast.success(`Promemoria inviati: ${res.data.sent_email} email, ${res.data.sent_wa} WhatsApp (${res.data.candidates} appuntamenti di domani)`);
                          }
                        } catch (err) {
                          toast.error(err.response?.data?.detail || 'Errore nell\'invio dei promemoria');
                        } finally { setSendingReminders(false); }
                      }}
                      data-testid="reminder-run-now-button"
                    >
                      <BellRing size={14} className="mr-1" /> {sendingReminders ? 'Invio...' : 'Invia ora'}
                    </Button>
                  </div>
                </div>
              )}
              <p className="text-xs text-muted-foreground pt-2 border-t">Ricorda di salvare dopo ogni modifica. I messaggi WhatsApp inviati compaiono anche nell'inbox del backoffice.</p>
            </CardContent>
          </Card>
        )}

        {/* Pagamenti Online (Stripe) */}
        {user?.role === 'admin' && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2" style={{fontFamily:'Space Grotesk'}}><CreditCard size={20} /> Pagamenti Online (Stripe)</CardTitle>
            </CardHeader>
            <CardContent className="space-y-1">
              <p className="text-sm text-muted-foreground pb-2">I pazienti possono pagare online al momento della prenotazione (saldo completo o acconto, configurabile per singolo servizio nella pagina Servizi). I rimborsi si gestiscono manualmente dalla dashboard Stripe.</p>
              {/* Chiave API Stripe */}
              <div className="py-3 border-t space-y-2">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium">Chiave API Stripe (Secret Key)</p>
                  {settings?.stripe_api_key_configured ? (
                    <Badge className={settings?.stripe_key_mode === 'live' ? 'bg-emerald-100 text-emerald-800 hover:bg-emerald-100' : 'bg-amber-100 text-amber-800 hover:bg-amber-100'} data-testid="stripe-key-status-badge">
                      Configurata — modalità {settings?.stripe_key_mode === 'live' ? 'LIVE' : 'TEST'}
                    </Badge>
                  ) : (
                    <Badge variant="secondary" data-testid="stripe-key-status-badge">Non configurata</Badge>
                  )}
                </div>
                <div className="relative">
                  <Input
                    type={showStripeKey ? 'text' : 'password'}
                    value={settings?.stripe_api_key || ''}
                    onChange={e => handleChange('stripe_api_key', e.target.value)}
                    placeholder={settings?.stripe_api_key_configured ? 'Chiave salvata — inserisci una nuova chiave solo per sostituirla' : 'sk_test_... oppure sk_live_...'}
                    className="pr-10"
                    data-testid="stripe-api-key-input"
                  />
                  <button type="button" onClick={() => setShowStripeKey(s => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" aria-label="Mostra/nascondi chiave">
                    {showStripeKey ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
                <p className="text-xs text-muted-foreground">La trovi su dashboard.stripe.com → Developers → API keys. Usa <strong>sk_test_...</strong> per le prove e <strong>sk_live_...</strong> per i pagamenti reali. Lasciando il campo vuoto la chiave salvata non viene modificata. La chiave inserita qui ha priorità su quella del server.</p>
              </div>
              <div className="flex items-center justify-between py-3 border-t">
                <div className="pr-4">
                  <p className="text-sm font-medium">Pagamenti online attivi</p>
                  <p className="text-xs text-muted-foreground">Se disattivato, nessun pagamento viene richiesto online anche se configurato sui servizi.</p>
                </div>
                <Checkbox checked={settings?.pagamenti_online_attivi === true} onCheckedChange={v => handleChange('pagamenti_online_attivi', v)} data-testid="payments-enabled-checkbox" />
              </div>
              <div className="flex items-center justify-between py-3 border-t">
                <div className="pr-4">
                  <p className="text-sm font-medium">Pagamento obbligatorio</p>
                  <p className="text-xs text-muted-foreground">Se attivo, dopo la prenotazione il paziente viene portato subito al pagamento. Se disattivo, il pagamento è facoltativo e può essere fatto anche in seguito da "I Miei Appuntamenti".</p>
                </div>
                <Checkbox checked={settings?.pagamento_obbligatorio === true} onCheckedChange={v => handleChange('pagamento_obbligatorio', v)} data-testid="payments-mandatory-checkbox" />
              </div>
            </CardContent>
          </Card>
        )}

        {/* SMTP Configuration */}
        {user?.role === 'admin' && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2" style={{fontFamily:'Space Grotesk'}}><Mail size={20} /> Configurazione Email (SMTP)</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground">Configura il server SMTP per l'invio delle email di verifica registrazione.</p>
              <div className="space-y-2">
                <Label>Email studio per notifiche prenotazioni</Label>
                <Input value={settings?.email_notifiche || ''} onChange={e => handleChange('email_notifiche', e.target.value)} placeholder="es. segreteria@studiomedicoboscovich.com" />
                <p className="text-xs text-muted-foreground">Riceverà notifiche su nuove prenotazioni, conferme, rifiuti e cancellazioni</p>
              </div>
              <div className="flex items-center justify-between py-3 border-b">
                <div>
                  <p className="text-sm font-medium">Verifica email attiva</p>
                  <p className="text-xs text-muted-foreground">Se attivo, i nuovi utenti riceveranno un codice di verifica via email</p>
                </div>
                <Checkbox checked={settings?.smtp_enabled === true} onCheckedChange={v => handleChange('smtp_enabled', v)} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2"><Label>Server SMTP</Label><Input value={settings?.smtp_host || ''} onChange={e => handleChange('smtp_host', e.target.value)} placeholder="es. smtp.gmail.com" /></div>
                <div className="space-y-2"><Label>Porta</Label><Input type="number" value={settings?.smtp_port || 587} onChange={e => handleChange('smtp_port', parseInt(e.target.value) || 587)} /></div>
              </div>
              <div className="space-y-2"><Label>Email mittente</Label><Input value={settings?.smtp_email || ''} onChange={e => handleChange('smtp_email', e.target.value)} placeholder="es. noreply@studiomedicoboscovich.com" /></div>
              <div className="space-y-2">
                <Label>Username SMTP (opzionale)</Label>
                <Input value={settings?.smtp_username || ''} onChange={e => handleChange('smtp_username', e.target.value)} placeholder="Solo se il login è diverso dall'email mittente (es. Brevo)" data-testid="smtp-username-input" />
                <p className="text-xs text-muted-foreground">Lascia vuoto per usare l'email mittente come login. Compilalo per servizi come Brevo/SendGrid dove il login è un account tecnico.</p>
              </div>
              <div className="space-y-2">
                <Label>Password SMTP / App Password</Label>
                <div className="relative">
                  <Input type={showSmtpPassword ? "text" : "password"} value={settings?.smtp_password || ''} onChange={e => handleChange('smtp_password', e.target.value)} placeholder="La password SMTP o App Password" className="pr-10" />
                  <button type="button" onClick={() => setShowSmtpPassword(p => !p)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-[#1F2A37] transition-colors">
                    {showSmtpPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>
              
              {/* Test email */}
              <div className="border rounded-lg p-4 bg-[hsl(var(--secondary))]/50 space-y-3">
                <p className="text-sm font-medium">Invia email di test</p>
                <p className="text-xs text-muted-foreground">Prima salva le impostazioni, poi invia un test per verificare che la configurazione funzioni.</p>
                <div className="flex gap-2">
                  <Input value={testEmail} onChange={e => setTestEmail(e.target.value)} placeholder="Inserisci email destinatario..." className="flex-1" />
                  <Button
                    size="sm"
                    disabled={testingSMTP || !testEmail}
                    onClick={async () => {
                      setTestingSMTP(true);
                      setSmtpTestLog(null);
                      try {
                        const res = await axios.post(`${API}/settings/test-email?to_email=${encodeURIComponent(testEmail)}`);
                        toast.success(res.data.message);
                        setSmtpTestLog({ success: true, message: res.data.message, steps: res.data.steps || [] });
                      } catch (err) {
                        const detail = err.response?.data?.detail || 'Errore sconosciuto';
                        const steps = err.response?.data?.steps || [];
                        toast.error(detail);
                        setSmtpTestLog({ success: false, message: detail, steps });
                      } finally { setTestingSMTP(false); }
                    }}
                    className="bg-[#238A92] hover:bg-[#0F5E66] text-white"
                  >
                    <Mail size={14} className="mr-1" /> {testingSMTP ? 'Invio...' : 'Invia Test'}
                  </Button>
                </div>
                {smtpTestLog && (
                  <div className={`border rounded-lg p-3 mt-2 ${smtpTestLog.success ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
                    <p className={`text-sm font-medium mb-2 ${smtpTestLog.success ? 'text-green-800' : 'text-red-800'}`}>
                      {smtpTestLog.success ? 'Invio riuscito' : 'Invio fallito'}
                    </p>
                    {smtpTestLog.steps.length > 0 && (
                      <div className="space-y-1">
                        {smtpTestLog.steps.map((step, i) => (
                          <div key={`step-${i}`} className="flex items-start gap-2">
                            <span className={`text-xs mt-0.5 ${step.ok ? 'text-green-600' : 'text-red-600'}`}>{step.ok ? '✓' : '✗'}</span>
                            <span className="text-xs text-gray-700">{step.label}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    {!smtpTestLog.success && (
                      <p className="text-xs text-red-700 mt-2 border-t border-red-200 pt-2">{smtpTestLog.message}</p>
                    )}
                  </div>
                )}
              </div>

              {/* Provider guide */}
              <details className="border rounded-lg">
                <summary className="cursor-pointer px-4 py-3 text-sm font-medium hover:bg-[hsl(var(--secondary))]/50">
                  Guida configurazione per provider email
                </summary>
                <div className="px-4 pb-4 space-y-4">
                  {/* Gmail */}
                  <div className="border rounded-lg p-3">
                    <p className="text-sm font-semibold text-[#1F2A37] mb-2">Gmail / Google Workspace</p>
                    <div className="text-xs text-muted-foreground space-y-1">
                      <p><strong>Server SMTP:</strong> smtp.gmail.com</p>
                      <p><strong>Porta:</strong> 587</p>
                      <p><strong>Email:</strong> il tuo indirizzo Gmail</p>
                      <p><strong>Password:</strong> devi usare una <strong>App Password</strong>, non la password normale</p>
                    </div>
                    <div className="mt-2 bg-amber-50 border border-amber-200 rounded p-2">
                      <p className="text-xs text-amber-800"><strong>Come creare una App Password Google:</strong></p>
                      <ol className="text-xs text-amber-700 list-decimal ml-4 mt-1 space-y-0.5">
                        <li>Vai su <a href="https://myaccount.google.com/security" target="_blank" rel="noopener noreferrer" className="text-[#238A92] underline">myaccount.google.com/security</a></li>
                        <li>Attiva la <strong>Verifica in 2 passaggi</strong> (obbligatoria)</li>
                        <li>Vai su <a href="https://myaccount.google.com/apppasswords" target="_blank" rel="noopener noreferrer" className="text-[#238A92] underline">myaccount.google.com/apppasswords</a></li>
                        <li>Seleziona &quot;Altra app&quot; → scrivi &quot;Studio Medico&quot; → clicca &quot;Genera&quot;</li>
                        <li>Copia la password di 16 caratteri e incollala nel campo &quot;Password SMTP&quot;</li>
                      </ol>
                    </div>
                  </div>
                  {/* Outlook/Office365 */}
                  <div className="border rounded-lg p-3">
                    <p className="text-sm font-semibold text-[#1F2A37] mb-2">Outlook / Microsoft 365</p>
                    <div className="text-xs text-muted-foreground space-y-1">
                      <p><strong>Server SMTP:</strong> smtp-mail.outlook.com</p>
                      <p><strong>Porta:</strong> 587</p>
                      <p><strong>Email:</strong> il tuo indirizzo Outlook</p>
                      <p><strong>Password:</strong> la tua password Microsoft (o App Password se hai 2FA)</p>
                    </div>
                  </div>
                  {/* Aruba */}
                  <div className="border rounded-lg p-3">
                    <p className="text-sm font-semibold text-[#1F2A37] mb-2">Aruba</p>
                    <div className="text-xs text-muted-foreground space-y-1">
                      <p><strong>Server SMTP:</strong> smtps.aruba.it</p>
                      <p><strong>Porta:</strong> 465 (SSL) o 587 (TLS)</p>
                      <p><strong>Email:</strong> il tuo indirizzo email Aruba</p>
                      <p><strong>Password:</strong> la password della casella email</p>
                    </div>
                  </div>
                  {/* Generic */}
                  <div className="border rounded-lg p-3">
                    <p className="text-sm font-semibold text-[#1F2A37] mb-2">Altro provider</p>
                    <div className="text-xs text-muted-foreground">
                      <p>Contatta il tuo provider email per ottenere: server SMTP, porta, credenziali di accesso.</p>
                    </div>
                  </div>
                </div>
              </details>
            </CardContent>
          </Card>
        )}

        <div className="flex justify-end pb-24">
          <Button onClick={handleSave} disabled={saving} className="bg-[#238A92] hover:bg-[#0F5E66] text-white px-8" data-testid="settings-save-btn">
            <Save size={14} className="mr-1" /> {saving ? 'Salvataggio...' : 'Salva Impostazioni'}
          </Button>
        </div>
      </div>

      {/* Create Operator Dialog */}
      <Dialog open={operatorDialogOpen} onOpenChange={setOperatorDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle style={{fontFamily:'Space Grotesk'}}>Crea Nuovo Operatore</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateOperator} className="space-y-4">
            {operatorError && (
              <Alert variant="destructive">
                <AlertCircle size={16} />
                <AlertDescription>{operatorError}</AlertDescription>
              </Alert>
            )}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Nome</Label><Input value={operatorForm.nome} onChange={e => setOperatorForm(p => ({...p, nome: e.target.value}))} required /></div>
              <div className="space-y-2"><Label>Cognome</Label><Input value={operatorForm.cognome} onChange={e => setOperatorForm(p => ({...p, cognome: e.target.value}))} required /></div>
            </div>
            <div className="space-y-2"><Label>Email</Label><Input type="email" value={operatorForm.email} onChange={e => setOperatorForm(p => ({...p, email: e.target.value}))} required /></div>
            <div className="space-y-2"><Label>Password</Label><Input type="password" value={operatorForm.password} onChange={e => setOperatorForm(p => ({...p, password: e.target.value}))} required placeholder="Minimo 6 caratteri" /></div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setOperatorDialogOpen(false)}>Annulla</Button>
              <Button type="submit" disabled={operatorSaving} className="bg-[#238A92] hover:bg-[#0F5E66] text-white">
                {operatorSaving ? 'Creazione...' : 'Crea Operatore'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

    </div>
  );
}
